import type { SurvivalPressureProfile } from "@/levels/types";

/** Deep-equality reverse lookup: which named ramp (if any) a given `SurvivalPressureProfile` came from - used only for display (picking the right blurb via `describePressureRamp()`); `LevelGame` itself never needs to know the name, only the profile's own fields. Every V2-generated spec's `pressureProfile` is always one of these three by construction, so this only ever returns `null` for a hand-built/synthetic profile (e.g. in a test). */
export function pressureRampIdFromProfile(profile: SurvivalPressureProfile): DailyPressureRampId | null {
  // Checks both the frozen V1/V2 table and V3's own (PR11) - a V3 day's
  // `pressureProfile` never matches a V2-table entry byte-for-byte (the
  // floors/reductions differ), so both must be tried for this to work
  // for every generator version's stored spec.
  for (const table of [DAILY_PRESSURE_PROFILES, DAILY_PRESSURE_PROFILES_V3]) {
    for (const id of Object.keys(table) as DailyPressureRampId[]) {
      const candidate = table[id];
      if (
        candidate.graceMs === profile.graceMs &&
        candidate.stageMs === profile.stageMs &&
        candidate.stepReductionMsPerStage === profile.stepReductionMsPerStage &&
        candidate.minStepMs === profile.minStepMs
      ) {
        return id;
      }
    }
  }
  return null;
}

/**
 * Named, tested anti-stalling pressure ramps a Daily recipe can choose
 * from (see `recipes.ts`) - each is a `SurvivalPressureProfile` (see that
 * type's doc for the exact math `LevelGame` applies). All three share the
 * same shape of guarantee: a real grace period before any pressure
 * applies, small fixed per-stage step reductions (never a jump), and a
 * floor comfortably above what's actually playable - `aggressive`'s
 * 80ms floor is still slower than `high_speed`'s existing V1 modifier
 * range (90-110ms), so nothing here asks for reflexes this engine
 * doesn't already consider fair.
 *
 * Time-to-floor from the default 150ms `baseStepMs` (150 - floor) /
 * reduction, in stages, times `stageMs`:
 *   - gentle:     (150-90)/4  = 15 stages × 15s = ~225s (~3.75 min) to floor
 *   - standard:   (150-85)/6  ≈ 11 stages × 10s = ~110s (~1.8 min) to floor
 *   - aggressive: (150-80)/8  ≈ 9  stages ×  7s = ~63s  (~1 min) to floor
 */
export type DailyPressureRampId = "gentle" | "standard" | "aggressive";

export const DAILY_PRESSURE_PROFILES: Record<DailyPressureRampId, SurvivalPressureProfile> = {
  gentle: { graceMs: 20_000, stageMs: 15_000, stepReductionMsPerStage: 4, minStepMs: 90 },
  standard: { graceMs: 15_000, stageMs: 10_000, stepReductionMsPerStage: 6, minStepMs: 85 },
  aggressive: { graceMs: 10_000, stageMs: 7_000, stepReductionMsPerStage: 8, minStepMs: 80 },
};

/**
 * PR11 (Speed Engine V2) - Generator V3's own pressure profiles. `DAILY_PRESSURE_PROFILES`
 * above is FROZEN (Generator V1/V2 compatibility - `buildCandidateV2()`
 * still reads it, byte-for-byte unchanged); V3's `buildCandidateV3()`
 * reads this table instead. Floors raised well clear of the new absolute
 * Daily caps (`GameConfig.speedEngine.dailyCaps`: 90ms normal / 80ms
 * temporary) so growth/RUSH debt still have real headroom to matter once
 * the ramp is fully engaged - the audited finding that motivated this
 * table: under the OLD floors (80-90ms), pressure alone already
 * saturated the new caps before growth/RUSH could ever contribute
 * anything. Every floor here stays below every V3 recipe's own
 * `baseStepMsMin` that can select it (`DAILY_RECIPES_V3`), which
 * `validateSpecV2()`'s existing `pressureProfile.minStepMs > baseStepMs`
 * check enforces per-day, and above `GameConfig.gateExit.minStepMs` - see
 * `recipes.ts`'s `DAILY_RECIPES_V3` for the exact per-recipe pairing.
 */
export const DAILY_PRESSURE_PROFILES_V3: Record<DailyPressureRampId, SurvivalPressureProfile> = {
  gentle: { graceMs: 20_000, stageMs: 15_000, stepReductionMsPerStage: 2, minStepMs: 140 },
  standard: { graceMs: 15_000, stageMs: 10_000, stepReductionMsPerStage: 3, minStepMs: 130 },
  aggressive: { graceMs: 10_000, stageMs: 7_000, stepReductionMsPerStage: 4, minStepMs: 125 },
};

/** Concise, honest pre-run copy - never a raw dump of the ramp's internal ms fields. */
export function describePressureRamp(rampId: DailyPressureRampId): string {
  switch (rampId) {
    case "gentle":
      return "Speed creeps up gradually the longer you survive without growing.";
    case "standard":
      return "Speed ramps up at a steady pace the longer you stall without growing.";
    case "aggressive":
      return "Speed ramps up quickly if you stall without growing - keep eating.";
  }
}
